/* @flow */

import * as React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { get, mapValues, memoize, pick, sortBy } from 'lodash/fp';
import AddressField from 'app/components/form/AddressField';
import Button from 'app/components/commons/Button';
import DateField from 'app/components/form/DateField';
import InputField from 'app/components/commons/InputField';
import Responsive from 'app/components/responsive/Responsive';
import CountrySelect from 'app/components/form/CountrySelect';
import t from 'app/utils/translate';
import { updateUserData } from 'app/actions/userActions';

type Props = {
  callingCode: string,
  country: number,
  countryCode: string,
  defaultCity: string,
  defaultDateOfBirth: string,
  defaultEmailAddress: string,
  defaultFirstName: string,
  defaultLastName: string,
  defaultPhoneNumber: string,
  defaultPostcode: string,
  defaultState: string,
  defaultStreetAddress: string,
  defaultUsername: string,
  emailVerified: boolean,
  enableMobileVerification: boolean,
  // idVerified: boolean,
  isSubmitting: boolean,
  onSubmit: Object => void,
  phoneVerified: boolean,
  lightContent: boolean,
  sportLevels: Object
};

type State = {
  city: string,
  dateOfBirth: string,
  emailAddress: string,
  firstName: string,
  lastName: string,
  phoneNumber: string,
  postcode: string,
  state: string,
  streetAddress: string,
  username: string
};

const FormRow = styled.div(({ theme }) => ({
  margin: '0 -10px',
  [theme.mediaQuery.tabletLandscapeAndUp]: {
    display: 'flex'
  }
}));

const FormItem = styled.div(({ theme }) => ({
  padding: '0 10px',
  [theme.mediaQuery.tabletLandscapeAndUp]: {
    flexBasis: 0,
    flexGrow: 1,
    flexShrink: 1
  }
}));

const Final = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
  paddingTop: '1rem'
});

const VerificationLabel = styled.div({
  display: 'flex'
});

const VerificationCheckmark = styled.span(({ theme }) => ({
  color: theme.color.success,
  marginLeft: 'auto'
}));

const VerificationLink = styled(Link)(({ theme }) => ({
  color: theme.color.primary,
  marginLeft: 'auto',
  textTransform: 'uppercase'
}));

class ProfileForm extends React.Component<Props, State> {
  props: Props;

  state: State = {
    city: this.props.defaultCity,
    dateOfBirth: this.props.defaultDateOfBirth,
    emailAddress: this.props.defaultEmailAddress,
    firstName: this.props.defaultFirstName,
    lastName: this.props.defaultLastName,
    phoneNumber: this.props.defaultPhoneNumber,
    postcode: this.props.defaultPostcode,
    state: this.props.defaultState,
    streetAddress: this.props.defaultStreetAddress,
    username: this.props.defaultUsername
  };

  handleSubmit = () => {
    this.props.onSubmit({
      address: this.state.streetAddress,
      city: this.state.city,
      // birth_date: this.state.dateOfBirth,
      email: this.state.emailAddress,
      // first_name: this.state.firstName,
      // last_name: this.state.lastName,
      name: this.state.username,
      phone: this.state.phoneNumber,
      postcode: this.state.postcode,
      state: this.state.state
    });
  };

  setFormField = memoize((key: string) => (value: string) => {
    this.setState({
      [key]: value
    });
  });

  setAddressFields = (fields: Object) => {
    this.setState(pick(['streetAddress', 'city', 'state', 'postcode'], fields));
  };

  render() {
    const {
      callingCode,
      country,
      countryCode,
      defaultEmailAddress,
      defaultPhoneNumber,
      defaultUsername,
      emailVerified,
      enableMobileVerification,
      // idVerified,
      isSubmitting,
      phoneVerified,
      lightContent,
      sportLevels
    } = this.props;

    const {
      city,
      dateOfBirth,
      emailAddress,
      firstName,
      lastName,
      phoneNumber,
      postcode,
      state,
      streetAddress,
      username
    } = this.state;

    const restrictedSports = Object.values(sportLevels || {}).filter(
      ({ restricted }) => restricted
    );

    return (
      <form>
        <FormRow>
          <FormItem>
            <InputField
              disabled
              label={t('First Name')}
              type="text"
              name="firstName"
              value={firstName}
              onChange={this.setFormField('firstName')}
              placeholder="Enter First Name"
              lightContent={lightContent}
            />
          </FormItem>
          <FormItem>
            <InputField
              disabled
              label={t('Last Name')}
              type="text"
              name="lastName"
              value={lastName}
              onChange={this.setFormField('lastName')}
              placeholder="Enter Last Name"
              lightContent={lightContent}
            />
          </FormItem>
        </FormRow>
        <FormRow>
          <FormItem>
            <DateField
              disabled
              label={t('Date of Birth')}
              onChange={this.setFormField('dateOfBirth')}
              value={dateOfBirth}
              lightContent={lightContent}
            />
          </FormItem>
          <FormItem>
            <InputField
              label={
                <VerificationLabel>
                  {t('Mobile Phone')}
                  {enableMobileVerification &&
                    defaultPhoneNumber &&
                    (phoneVerified ? (
                      <VerificationCheckmark className="ion-checkmark-circled" />
                    ) : (
                      <VerificationLink to={{ search: '?modal=verify-phone' }}>
                        Verify
                      </VerificationLink>
                    ))}
                </VerificationLabel>
              }
              prefix={callingCode}
              type="tel"
              placeholder="Enter Mobile Number"
              name="phoneNumber"
              disabled={phoneVerified}
              value={phoneNumber}
              onChange={this.setFormField('phoneNumber')}
              lightContent={lightContent}
            />
          </FormItem>
        </FormRow>
        <FormRow>
          <FormItem>
            <InputField
              label={t('Username')}
              type="text"
              name="username"
              disabled={!!defaultUsername}
              value={username}
              onChange={this.setFormField('username')}
              placeholder="Enter Username"
              lightContent={lightContent}
            />
          </FormItem>
          <FormItem>
            <InputField
              label={
                <VerificationLabel>
                  {t('Email Address')}
                  {defaultEmailAddress &&
                    (emailVerified ? (
                      <VerificationCheckmark className="ion-checkmark-circled" />
                    ) : (
                      <VerificationLink to={{ search: '?modal=verify-email' }}>
                        Verify
                      </VerificationLink>
                    ))}
                </VerificationLabel>
              }
              type="email"
              name="emailAddress"
              disabled={emailVerified}
              value={emailAddress}
              onChange={this.setFormField('emailAddress')}
              placeholder="Enter Email Address"
              lightContent={lightContent}
            />
          </FormItem>
        </FormRow>
        <FormRow>
          <FormItem>
            <CountrySelect disabled value={country} lightContent={lightContent} />
          </FormItem>
        </FormRow>
        <FormRow>
          <FormItem>
            <AddressField
              city={city}
              countryId={country}
              countryCode={countryCode}
              postcode={postcode}
              onChange={this.setAddressFields}
              streetAddress={streetAddress}
              state={state}
              lightContent={lightContent}
            />
          </FormItem>
        </FormRow>
        {restrictedSports.length > 0 && (
          <React.Fragment>
            <p style={{ marginTop: 20 }}>
              You are ineligible to enter casual contests for the following sports;
            </p>
            <ul>
              {sortBy('name', restrictedSports).map(({ name }) => (
                <li>{name}</li>
              ))}
            </ul>
          </React.Fragment>
        )}
        <Responsive tabletPortrait tabletLandsape desktop>
          {isLargeScreen => (
            <Final>
              <Button onClick={this.handleSubmit} disabled={isSubmitting} block={!isLargeScreen}>
                {t('Update profile')}
              </Button>
            </Final>
          )}
        </Responsive>
      </form>
    );
  }
}

const connector = connect(
  state => ({
    ...mapValues(x => x(state.user.currentUser), {
      country: get('country.id'),
      countryCode: get('country.iso_alpha_2_code'),
      callingCode: get('country.calling_code'),
      defaultCity: get('city'),
      defaultDateOfBirth: get('birth_date'),
      defaultEmailAddress: get('email'),
      defaultFirstName: get('first_name'),
      defaultLastName: get('last_name'),
      defaultPhoneNumber: get('phone'),
      defaultPostcode: get('postcode'),
      defaultState: get('state'),
      defaultStreetAddress: get('address'),
      defaultUsername: get('name'),
      emailVerified: get('email_verified'),
      phoneVerified: get('phone_verified'),
      idVerified: get('id_verified')
    }),
    enableMobileVerification: state.settings.enableMobileVerification,
    isSubmitting: state.user.updatingUser,
    lightContent: !state.settings.darkMode,
    sportLevels: get('user.currentUser.sport_levels', state)
  }),
  {
    onSubmit: updateUserData
  }
);

export default connector(ProfileForm);
